import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AgentDesktopState } from '@agent/agent-interface/agent-desktop-routing.module';
import { MessageType } from '@agent/agent-interface/contact-view/social-network/model/message-type';
import { SocialNetworkMessage } from '@agent/agent-interface/contact-view/social-network/model/social-network-message';
import { SocialNetworkTabService } from '@agent/agent-interface/contact-view/social-network/services/social-network-tab.service';
import { IAgentConfigDeprecatedService } from '@agent/core/agent/agent-config-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { DataBuilder } from '@agent/core/contact/util/data-builder';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { AppExternalService } from '@commons/core/ui/app-external.service';
import { IContact } from '@commons/models/contact';
import { ChannelUtil } from '@commons/util/helper/channel';
import { RxUtil } from '@commons/util/utilities/rx';
import { IPromise, ITimeoutService } from 'angular';
import { i18n } from 'i18next';
import { BehaviorSubject, filter, Observable, of, switchMap, withLatestFrom } from 'rxjs';

import {
  AgentConfigFacade,
  Booking,
  BookingFacade,
  BookingStatus,
  ChannelConfig,
  ChannelType,
  COMETD_NEW_SOCIAL_NETWORK_MESSAGE,
  Configuration,
  CSR_FACADE_PREFIX,
  Customer,
  Logger,
  MessagingService,
  OpeningChannel,
  ToastClass,
  ToasterFacade,
} from '@contact/upgrade';

import { SocialNetworkModel } from '../model/social-network-model';
import { AgentBookinSocialNetworkInprocessState } from '../state/agent-booking-social-network-routing.module';

const MODULE_NAME = 'socialNetworkFrameService';

export const SOCIAL_NETWORK_PREFIX = 'SOCIAL_NETWORK_';
export const MESSAGE_ENDPOINT = '/app/socialnetwork.message';
const SOCIAL_NETWORK_DEFAULT_MAX_COUNT = 4;
const STATE_DURATION_FOR_ANIMATION = 1000;
export const SOCIAL_NETWORK_FRAME_NAME = 'socialNetwork';
export const SOCIAL_NETWORK_CONTAINER_ID = 'social-network-iframe';

export interface ISocialNetworkFrameService {
  /**
   * Subscribtion object for handling last closed social network event
   */
  isHaveOpenSocialNetwork$: BehaviorSubject<boolean>;
  /**
   * selected social network
   */
  readonly activeSocialNetwork?: SocialNetworkModel;

  /**
   * List of chat conversations currently linked to the agent
   */
  readonly socialNetworkList: Array<SocialNetworkModel>;

  getSocialNetworkContactUrl(): IPromise<any>;

  /**
   * End the socialnetwork for the given customer
   * @param {string} customerId id of the customer
   * @return {boolean} true if it succeeds, false otherwise
   */
  removeSocialNetwork(customerId: string): boolean;

  closeSocialNetwork(customerId: string): boolean;

  /**
   * Make an available social network selected
   * @param {SocialNetworkModel} socialNetwork  the social network  to select
   */
  selectSocialNetwork(socialNetwork: SocialNetworkModel);

  /**
   * Check exist open socialnetwork or not
   * @return {boolean} true if it exist open socialnetwork, false otherwise
   */
  haveOpenSocialNetwork(): boolean;

  /**
   * Check if an agent can accept a new social conversation
   * @return {boolean} true if possible, false otherwise
   */
  csrCanAccept(): boolean;

  /**
   * Get SocialNetworkModel from responseId
   * @param responseId
   * @return SocialNetworkModel or null
   */
  getSocialNetworkByResponseId(responseId: string): SocialNetworkModel | undefined;

  show(contactId: string): void;

  hide(nameApp: string): void;

  /**
   * Update number of social network who is in PostTreatmentDurationExceeded
   */
  updateSocialNetworkHavePostTreatmentDurationExceeded(): void;

  /**
   * Update number of social network who is in PostTreatment
   */
  updateSocialNetworkInQualificationCount(): void;

  hasPostTreatment(): boolean;

  sendPostMessageToFrame(respondeId: string, insertText: string): void;
}

export class SocialNetworkFrameService implements ISocialNetworkFrameService {
  static $inject = [
    'logger',
    'httpDeprecated',
    'socialNetworkTabService',
    '$timeout',
    'contactDeprecatedService',
    '$i18next',
    'agentConfigFacade',
    'toasterFacade',
    'messaging',
    'externalApplications',
    'agentConfigDeprecatedService',
    'configuration',
    'contactFacade',
    'timerService',
    'agentDesktopRouter',
  ];
  public name = MODULE_NAME;
  isHaveOpenSocialNetwork$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _socialNetworkMap: Map<string, SocialNetworkModel> = new Map<string, SocialNetworkModel>();
  private _socialNetworkList: Array<SocialNetworkModel> = [];
  private _activeSocialNetworkCustomerId: string | undefined;
  private _channelConfig: ChannelConfig | undefined;

  public dataBuilder: DataBuilder;
  private PARTIAL_DATA_COUNT = 1;

  private readonly _socialNetworkInQualificationCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private readonly _socialNetworkHavePostTreatmentDurationExceeded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly _socialNetworkNewMessageCount$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  constructor(
    private readonly logger: Logger,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly tabService: SocialNetworkTabService,
    private readonly $timeout: ITimeoutService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly $i18next: i18n,
    private readonly agentConfigFacade: AgentConfigFacade,
    private readonly toasterFacade: ToasterFacade,
    private readonly messaging: MessagingService,
    private readonly externalAppService: AppExternalService,
    private readonly agentConfigDeprecatedService: IAgentConfigDeprecatedService,
    private readonly configuration: Configuration,
    private readonly contactFacade: BookingFacade,
    private readonly router: AgentDesktopRouter
  ) {
    this.dataBuilder = new DataBuilder(this.logger, (id, data) => this.onDataCompleted(id, data), this.PARTIAL_DATA_COUNT);

    this.contactFacade.created$
      .pipe(filter(({ booking }) => booking.channel.type === ChannelType.SOCIAL_NETWORK))
      .subscribe(({ booking, current }) => this.manageSocialNetworkIncomingAccepting(booking, current));

    this.contactFacade.success$
      .pipe(
        filter(
          ({ booking, previous, current }) =>
            booking.channel.type === ChannelType.SOCIAL_NETWORK &&
            ((current === BookingStatus.ALLOCATED && previous === BookingStatus.ACCEPTING) || current === BookingStatus.IN_PROCESS)
        )
      )
      .subscribe(({ booking, current }) => this.manageSocialNetworkIncomingAcceptation(booking, current));

    this.contactFacade.success$
      .pipe(filter(({ booking, current }) => booking.channel.type === ChannelType.SOCIAL_NETWORK && current === BookingStatus.CLOSED))
      .subscribe(event => {
        const SNModel = this.getSocialNetworkByResponseId(event.booking.responseId);
        if (!!SNModel) {
          this.removeSocialNetwork(SNModel.customerId);
        }
      });

    this.contactFacade.success$
      .pipe(
        filter(({ booking, current }) => booking.channel.type === ChannelType.SOCIAL_NETWORK && current === BookingStatus.POST_PROCESSING)
      )
      .subscribe(event => {
        const SNModel = this.getSocialNetworkByResponseId(event.booking.responseId);
        if (!!SNModel) {
          this.canActivePostTreatment(SNModel.customerId);
        }
      });

    this.contactDeprecatedService
      .contactUpdated$()
      .pipe(
        switchMap(contact =>
          of({}).pipe(
            withLatestFrom(
              RxUtil.fromPromise(() => this.contactDeprecatedService.getCustomer(contact.responseId)),
              (_, customer) => ({ contact, customer })
            )
          )
        )
      )
      .subscribe(({ contact, customer }) => this.updateInfoContact(contact, customer));
    this.messaging
      .forRemote()
      .listen$(COMETD_NEW_SOCIAL_NETWORK_MESSAGE)
      .subscribe(dto => this.incrementMessageTab(dto));

    this.agentConfigFacade.getConfig(ChannelUtil.INCOMING_SOCIAL_NETWORK).subscribe(config => (this._channelConfig = config));
  }

  private manageSocialNetworkIncomingAccepting(contact: IContact, current: BookingStatus) {
    let toastContent = '';
    if (this._channelConfig && this._channelConfig.acceptation) {
      if (!!contact.forward && !!contact.forward.agentName) {
        if (contact.conference) {
          toastContent = this.$i18next.t(
            BookingStatus.ACCEPTING ? 'agent:toast.social_network_conference_by' : 'agent:toast.join_social_network_conference_by',
            { name: this.$i18next.t(contact.forward.agentName) }
          );
        } else {
          toastContent = this.$i18next.t(
            BookingStatus.ACCEPTING ? 'agent:toast.social_network_transfer_by' : 'agent:toast.social_network_conversation_transferred_by',
            { name: this.$i18next.t(contact.forward.agentName) }
          );
        }
      } else {
        toastContent = this.$i18next.t(
          BookingStatus.ACCEPTING ? 'agent:toast.new_social_network_conversation' : 'agent:toast.social_network_conversation_added',
          { name: this.$i18next.t(contact.customerName) }
        );
      }
      if (current === BookingStatus.ALLOCATED) {
        this.toasterFacade.notification({
          message: toastContent,
          cssClass: ToastClass.SOCIAL_NETWORK,
          duration: 5000,
        });
      }
    }
  }

  private manageSocialNetworkIncomingAcceptation(contact: IContact, status: BookingStatus) {
    if (
      status === BookingStatus.ALLOCATED &&
      this._channelConfig &&
      (!this._channelConfig.acceptation || !this._channelConfig.acceptation.presentCall)
    ) {
      let toastContent = '';
      if (!!contact.forward && !!contact.forward.agentName) {
        if (contact.conference) {
          toastContent = this.$i18next.t('agent:toast.join_social_network_conference_by', {
            name: this.$i18next.t(contact.forward.agentName),
          });
        } else {
          toastContent = this.$i18next.t('agent:toast.social_network_conversation_transferred_by', {
            name: this.$i18next.t(contact.forward.agentName),
          });
        }
      } else {
        toastContent = this.$i18next.t('agent:toast.social_network_conversation_added', { name: this.$i18next.t(contact.customerName) });
      }
      this.toasterFacade.notification({
        message: toastContent,
        cssClass: ToastClass.SOCIAL_NETWORK,
        duration: 5000,
      });
    } else {
      this.dataBuilder.savePartialData(SOCIAL_NETWORK_PREFIX + contact.connectionId, contact);
      this.contactDeprecatedService
        .getSelectedContact()
        .then(currentSelectedContact => this.selectSocialNetworkContact(contact, currentSelectedContact))
        .catch(() => this.selectSocialNetworkContact(contact));
    }
  }

  private selectSocialNetworkContact(socialNetworkContact: IContact, previousContact?: IContact) {
    if (socialNetworkContact === previousContact) {
      this.contactFacade.select(socialNetworkContact.responseId);
    }
    let openingChannel = this.agentConfigDeprecatedService.openingChannels;
    if (openingChannel.length === 0 && socialNetworkContact.channel.type === ChannelType.SOCIAL_NETWORK) {
      if (this.checkContactSocialNetworkChannelType(previousContact)) {
        this.contactFacade.select(socialNetworkContact.responseId);
      }
    } else {
      openingChannel.forEach((mChannel: OpeningChannel) => {
        if (
          mChannel.channel.type === ChannelType.SOCIAL_NETWORK &&
          (!previousContact || ChannelUtil.isOpeningWith(mChannel, previousContact.channel))
        ) {
          if (this.checkContactSocialNetworkChannelType(previousContact)) {
            this.contactFacade.select(socialNetworkContact.responseId);
          }
        }
      });
    }
  }

  private updateInfoContact(contact: IContact, customer: Customer | undefined) {
    this.socialNetworkList.forEach(tab => {
      if (tab.responseId === contact.responseId) {
        if (!!customer && !!customer.firstname && !!customer.lastname) {
          let labelName: string = '';
          if (!!customer.firstname) {
            labelName += customer.firstname.substr(0, 1).toUpperCase() + customer.firstname.substr(1).toLowerCase() + ' ';
          }
          if (!!customer.lastname) {
            labelName += customer.lastname.toUpperCase();
          }
          tab.updateNameContact(labelName);
          this.tabService.socialNetworkName = labelName;
        }
      }
    });
  }

  private incrementMessageTab(dto: any): void {
    this.socialNetworkList.forEach(tab => {
      if (tab.threadId === dto.contactUuid) {
        let msg: SocialNetworkMessage = new SocialNetworkMessage(
          MessageType.SOCIAL_NETWORK,
          dto.message,
          '',
          '',
          undefined,
          undefined,
          dto.receiveMessageDate
        );
        if (this._activeSocialNetworkCustomerId !== tab.customerId) {
          tab.increaseUnreadCount();
        }
        tab.addMessage(msg);

        this.tabService.lastMessageDate = new Date(dto.receiveMessageDate);
        this.tabService.setNewConversationCount(this._socialNetworkList);
      }
    });
  }
  private checkContactSocialNetworkChannelType(previousContact?: IContact): boolean {
    if (!previousContact) {
      return true;
    } else if (previousContact.channel.type !== ChannelType.SOCIAL_NETWORK) {
      return true;
    }
    return false;
  }

  /**
   * @inheritDoc
   */
  public getSocialNetworkContactUrl(): IPromise<any> {
    return this.httpDeprecated.asPromise().post(CSR_FACADE_PREFIX + '/getSocialNetworkEndpoint');
  }

  /**
   * @see ISocialNetworkFrameService.activeSocialNetwork
   */
  public get activeSocialNetwork(): SocialNetworkModel | undefined {
    return this._socialNetworkMap.get(this._activeSocialNetworkCustomerId || '');
  }

  /**
   * @inheritDoc
   */
  public removeSocialNetwork(customerId: string): boolean {
    const social = this._socialNetworkMap.get(customerId);
    if (social) {
      if (social.hasPostTreatment && !social.inPostTreatmentMode) {
        return true;
      }
      this._socialNetworkMap.delete(customerId);
      const socialNetworkIndex = this._socialNetworkList.indexOf(social);
      this._socialNetworkList.splice(socialNetworkIndex, 1);
      if (this._activeSocialNetworkCustomerId === customerId) {
        this._activeSocialNetworkCustomerId = undefined;
      }

      this.updateSocialNetworkTabStatus();
      this.tabService.computeLastMessageDateForAllSocialNetworks(this._socialNetworkList);
      this.isHaveOpenSocialNetwork$.next(this._socialNetworkList.length > 0);
      this.updateSocialNetworkInQualificationCount();
      return true;
    }

    return false;
  }

  public canActivePostTreatment(customerId: string): boolean {
    const social = this._socialNetworkMap.get(customerId);
    if (social) {
      if (social.hasPostTreatment && !social.inPostTreatmentMode) {
        social.inPostTreatmentMode = true;
        this.updateSocialNetworkInQualificationCount();
        return true;
      }
    }
    return false;
  }

  /**
   * @inheritDoc
   */
  public closeSocialNetwork(customerId: string): boolean {
    const social = this._socialNetworkMap.get(customerId);
    if (social) {
      this.contactDeprecatedService.hangUp(social.responseId);
      this.removeSocialNetwork(customerId);
      return true;
    }

    return false;
  }

  /**
   * @inheritDoc
   */
  public selectSocialNetwork(socialNetwork: SocialNetworkModel) {
    if (socialNetwork.customerId !== this._activeSocialNetworkCustomerId) {
      this._activeSocialNetworkCustomerId = socialNetwork.customerId;
      socialNetwork.tab.resetUnreadCount();
      this.tabService.setNewConversationCount(this._socialNetworkList);
      this.contactFacade.select(socialNetwork.responseId);
      this.router.navigateToTile([
        AgentDesktopState.BOOKING,
        socialNetwork.responseId,
        BookingStatus.IN_PROCESS.toLowerCase(),
        AgentBookinSocialNetworkInprocessState.conversation,
      ]);
    }
  }
  /**
   * Update the social network status bar (social count / social name)
   */
  protected updateSocialNetworkTabStatus(): void {
    this.tabService.conversationCount = this._socialNetworkList.length;
    if (this._socialNetworkList.length === 1) {
      this.tabService.socialNetworkName = this._socialNetworkList[0].tab.socialNetworkName;
    }
  }

  /**
   * @inheritDoc
   */
  public haveOpenSocialNetwork(): boolean {
    return this._socialNetworkList.length > 0;
  }

  get socialNetworkNewMessageCount$(): Observable<number> {
    return this._socialNetworkNewMessageCount$.asObservable();
  }

  public updateSocialNetworkNewMessageCount(): void {
    this._socialNetworkNewMessageCount$.next(this._socialNetworkList.reduce((prev, curr) => prev + curr.tab.newMessagesCount, 0));
  }

  get socialNetworkInQualificationCount$(): Observable<number> {
    return this._socialNetworkInQualificationCount$.asObservable();
  }

  public updateSocialNetworkInQualificationCount(): void {
    this._socialNetworkInQualificationCount$.next(
      this._socialNetworkList.filter(socialNetwork => socialNetwork.inPostTreatmentMode).length
    );
  }

  get socialNetworkHavePostTreatmentDurationExceeded$(): Observable<boolean> {
    return this._socialNetworkHavePostTreatmentDurationExceeded$.asObservable();
  }

  public updateSocialNetworkHavePostTreatmentDurationExceeded(): void {
    this._socialNetworkHavePostTreatmentDurationExceeded$.next(this.socialNetworkList.some(social => social.inPostTreatmentExceededMode));
  }

  /**
   * @inheritDoc
   */
  public csrCanAccept(): boolean {
    return this._socialNetworkList.length < SOCIAL_NETWORK_DEFAULT_MAX_COUNT;
  }

  /**
   * @inheritDoc
   */
  public get socialNetworkList(): Array<SocialNetworkModel> {
    return this._socialNetworkList;
  }

  /**
   * @inheritDoc
   */
  private addSocialNetwork(customerId: string, contact: Booking) {
    const social = this._socialNetworkMap.get(customerId);
    if (this.csrCanAccept() && !social) {
      this.contactDeprecatedService.getCustomer(contact.responseId).then(customer => {
        const socialModel = new SocialNetworkModel(customerId, contact, customer);
        socialModel.responseId = contact.responseId;
        this._socialNetworkMap.set(customerId, socialModel);
        this._socialNetworkList.push(socialModel);
        this.$timeout(() => {
          socialModel.isNew = false;
        }, STATE_DURATION_FOR_ANIMATION);
        this.updateSocialNetworkTabStatus();
        this.tabService.lastMessageDate = new Date(socialModel.tab.lastMessageTime);
        this.isHaveOpenSocialNetwork$.next(this._socialNetworkList.length > 0);

        this.contactFacade.process(contact.responseId);
      });
    }
  }

  /**
   * @inheritDoc
   */
  public getSocialNetworkByResponseId(responseId: string): SocialNetworkModel | undefined {
    if (responseId) {
      return this._socialNetworkList.find(c => c.responseId === responseId);
    }
    return undefined;
  }

  show(responseId: string): void {
    let htmlElement = document.getElementById(SOCIAL_NETWORK_CONTAINER_ID);
    if (!!responseId && htmlElement) {
      this.externalAppService.positionApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + responseId, htmlElement);
      this.externalAppService.showApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + responseId);
    }
  }

  hide(responseId: string): void {
    this.externalAppService.hideApplication(SOCIAL_NETWORK_FRAME_NAME + '_' + +responseId);
  }

  onDataCompleted(id: string, data: Array<any>) {
    this.logger.debug(`contactId (${id}).`);
    const contact = data.find(part => 'responseId' in part);
    if (contact) {
      this.addSocialNetwork(contact.responseId, contact);
    }
  }

  /**
   * @inheritDoc
   */
  hasPostTreatment(): boolean {
    let isInPostTreatment = this._socialNetworkList.filter(value => value.inPostTreatmentMode);
    return isInPostTreatment.length > 0;
  }

  sendPostMessageToFrame(respondeId: string, insertText: string): void {
    let frame = this.externalAppService.getApplicationContainer(SOCIAL_NETWORK_FRAME_NAME + '_' + respondeId).getOrNull();
    if (!!frame && frame.contentWindow) {
      frame.contentWindow.postMessage(
        {
          name: 'smcc:reply_assistant',
          data: { message: insertText },
        },
        this.configuration.dimeloBaseUrl || ''
      ); // Url de l'instance DD
    }
  }
}

export default MODULE_NAME;
